import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ArrowPathIcon,
  CpuChipIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { PageHeader } from '@/components/common/PageHeader';
import { useTranslation } from '@/store/languageStore';
import { usePermissionsStore } from '@/store/permissionsStore';
import { useAuthStore } from '@/store/authStore';
import api from '@/services/api/client';

interface WikiInfo {
  filename: string;
  sourceDate: string | null;
  deviceFamily: string;
  sha1: string | null;
}

interface StatusPayload {
  wiki: WikiInfo;
  current: {
    sourceDate: string | null;
    filename: string | null;
    importedAt: string | null;
    rowCount: number;
    deviceFamily: string | null;
    parameterKeys: string[];
  } | null;
  hasNewer: boolean;
}

interface VehicleRow {
  id: number;
  make: string;
  model: string;
  modelYear: string | null;
  fuelType: string | null;
  yearFrom: number | null;
  yearTo: number | null;
  deviceFamily: string;
  parameters: Record<string, boolean>;
}

interface DetailPayload {
  vehicle: VehicleRow;
  kbHits: Array<{ sourceId: number; title: string; snippet: string; pageUrl: string }>;
  fleetMatches: Array<{
    registrationNumber: string | null;
    brand: string | null;
    model: string | null;
    year: number | null;
    forceNonObd: boolean;
  }>;
}

function paramLabel(key: string): string {
  return key.replace(/_/g, ' ');
}

const ObdVehicleListPage: React.FC = () => {
  const { t } = useTranslation();
  const canSync =
    useAuthStore((s) => s.user?.is_global_admin === true) ||
    usePermissionsStore((s) => s.hasActionAccess('ADMIN_OBD_VEHICLES', 'SYNC'));

  const [status, setStatus] = useState<StatusPayload | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [searchParams] = useSearchParams();
  const [makes, setMakes] = useState<string[]>([]);
  const [make, setMake] = useState(() => searchParams.get('make') ?? '');
  const [model, setModel] = useState('');
  const [year, setYear] = useState('');
  const [parameter, setParameter] = useState('');
  const [rows, setRows] = useState<VehicleRow[]>([]);
  const [total, setTotal] = useState(0);
  const [parameterKeys, setParameterKeys] = useState<string[]>([]);
  const [searching, setSearching] = useState(false);
  const [detail, setDetail] = useState<DetailPayload | null>(null);

  const loadStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const response = await api.get('/api/admin/obd-vehicles/status', { timeout: 60000 });
      setStatus(response.data?.data ?? null);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || t('obdVehicles.checkFailed'));
    } finally {
      setStatusLoading(false);
    }
  }, [t]);

  const loadMakes = useCallback(async () => {
    try {
      const response = await api.get('/api/admin/obd-vehicles/makes');
      setMakes(response.data?.data ?? []);
    } catch {
      setMakes([]);
    }
  }, []);

  const runSearch = useCallback(async () => {
    setSearching(true);
    try {
      const params = new URLSearchParams();
      if (make) params.set('make', make);
      if (model) params.set('model', model);
      if (year) params.set('year', year);
      if (parameter) params.set('parameter', parameter);
      const response = await api.get(`/api/admin/obd-vehicles/search?${params.toString()}`);
      setRows(response.data?.data?.rows ?? []);
      setTotal(response.data?.data?.total ?? 0);
      setParameterKeys(response.data?.data?.parameterKeys ?? []);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || t('obdVehicles.searchFailed'));
    } finally {
      setSearching(false);
    }
  }, [make, model, year, parameter, t]);

  useEffect(() => {
    void loadMakes();
    void runSearch();
    // Initial load only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSync = async (force = false) => {
    setSyncing(true);
    try {
      const response = await api.post('/api/admin/obd-vehicles/sync', { force }, { timeout: 180000 });
      const result = response.data?.data;
      if (result?.skipped) {
        toast.success(t('obdVehicles.alreadyLatest'));
      } else {
        toast.success(t('obdVehicles.imported', { count: result?.rowCount ?? 0 }));
      }
      await Promise.all([loadStatus(), loadMakes(), runSearch()]);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || t('obdVehicles.syncFailed'));
    } finally {
      setSyncing(false);
    }
  };

  const openDetail = async (id: number) => {
    try {
      const response = await api.get(`/api/admin/obd-vehicles/${id}`);
      setDetail(response.data?.data ?? null);
    } catch {
      toast.error(t('obdVehicles.detailFailed'));
    }
  };

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title={t('obdVehicles.title')}
        description={t('obdVehicles.description')}
        icon={<CpuChipIcon className="w-6 h-6 text-primary" />}
        backTo="/system/administration"
        actions={
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => void loadStatus()}
              disabled={statusLoading}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-bgContainer text-sm"
            >
              <MagnifyingGlassIcon className="w-4 h-4" />
              {t('obdVehicles.checkNow')}
            </button>
            {canSync && (
              <button
                type="button"
                onClick={() => void handleSync(true)}
                disabled={syncing}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-primary text-white text-sm"
              >
                <ArrowPathIcon className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                {t('obdVehicles.import')}
              </button>
            )}
          </div>
        }
      />

      <div className="rounded-xl border border-border bg-bgContainer p-4 text-sm space-y-2">
        <p className="text-textSecondary">{t('obdVehicles.deviceFamilyNote')}</p>
        {statusLoading && <p>{t('common.loading')}</p>}
        {status && (
          <div className="flex flex-wrap gap-4">
            <span>
              {t('obdVehicles.wikiDate')}: <strong>{status.wiki.sourceDate ?? '–'}</strong>
            </span>
            <span>
              {t('obdVehicles.ourDate')}: <strong>{status.current?.sourceDate ?? t('obdVehicles.none')}</strong>
            </span>
            <span>
              {t('obdVehicles.rows')}: <strong>{status.current?.rowCount ?? 0}</strong>
            </span>
            {status.hasNewer && (
              <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                {t('obdVehicles.newerAvailable')}
              </span>
            )}
          </div>
        )}
      </div>

      <form
        className="grid grid-cols-1 md:grid-cols-5 gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          void runSearch();
        }}
      >
        <select
          value={make}
          onChange={(e) => setMake(e.target.value)}
          className="rounded-lg border border-border bg-bgContainer px-3 py-2"
        >
          <option value="">{t('obdVehicles.allMakes')}</option>
          {makes.map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </select>
        <input
          value={model}
          onChange={(e) => setModel(e.target.value)}
          placeholder={t('obdVehicles.model')}
          className="rounded-lg border border-border bg-bgContainer px-3 py-2"
        />
        <input
          value={year}
          onChange={(e) => setYear(e.target.value)}
          placeholder={t('obdVehicles.year')}
          className="rounded-lg border border-border bg-bgContainer px-3 py-2"
        />
        <select
          value={parameter}
          onChange={(e) => setParameter(e.target.value)}
          className="rounded-lg border border-border bg-bgContainer px-3 py-2"
        >
          <option value="">{t('obdVehicles.allParameters')}</option>
          {parameterKeys.map((key) => (
            <option key={key} value={key}>
              {paramLabel(key)}
            </option>
          ))}
        </select>
        <button type="submit" className="rounded-lg bg-primary text-white px-3 py-2">
          {t('common.search')}
        </button>
      </form>

      <p className="text-sm text-textSecondary">
        {searching ? t('common.loading') : t('obdVehicles.resultCount', { count: total })}
        {parameterKeys.length > 0 ? ` · ${t('obdVehicles.scrollHint')}` : ''}
      </p>

      <div className="overflow-x-auto max-w-full overscroll-x-contain rounded-xl border border-border">
        <table className="w-max min-w-full text-sm">
          <thead className="bg-bgSecondary text-textLight text-left">
            <tr>
              <th className="px-3 py-2 whitespace-nowrap">{t('obdVehicles.make')}</th>
              <th className="px-3 py-2 whitespace-nowrap">{t('obdVehicles.model')}</th>
              <th className="px-3 py-2 whitespace-nowrap">{t('obdVehicles.year')}</th>
              <th className="px-3 py-2 whitespace-nowrap">{t('obdVehicles.fuelType')}</th>
              {parameterKeys.map((key) => (
                <th key={key} className="px-3 py-2 whitespace-nowrap">
                  {paramLabel(key)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.id}
                className="border-t border-border hover:bg-bgLight cursor-pointer"
                onClick={() => void openDetail(row.id)}
              >
                <td className="px-3 py-2 whitespace-nowrap">{row.make}</td>
                <td className="px-3 py-2 whitespace-nowrap">{row.model}</td>
                <td className="px-3 py-2 whitespace-nowrap">{row.modelYear ?? '–'}</td>
                <td className="px-3 py-2 whitespace-nowrap">{row.fuelType ?? '–'}</td>
                {parameterKeys.map((key) => (
                  <td key={key} className="px-3 py-2 text-center">
                    {row.parameters[key] ? '+' : ''}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {detail && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-bgContainer rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-5 space-y-4">
            <div className="flex justify-between items-start">
              <h2 className="text-lg font-semibold">
                {detail.vehicle.make} {detail.vehicle.model} {detail.vehicle.modelYear}
              </h2>
              <button type="button" onClick={() => setDetail(null)}>
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <p className="text-xs text-textTertiary">{t('obdVehicles.deviceFamily')}: {detail.vehicle.deviceFamily}</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(detail.vehicle.parameters).map(([key, on]) => (
                <span
                  key={key}
                  className={`px-2 py-1 rounded text-xs ${on ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300' : 'bg-bgSecondary text-textTertiary'}`}
                >
                  {paramLabel(key)} {on ? '+' : '–'}
                </span>
              ))}
            </div>
            {detail.kbHits.length > 0 && (
              <div>
                <h3 className="font-medium mb-2">{t('obdVehicles.relatedKb')}</h3>
                <ul className="space-y-1 text-sm">
                  {detail.kbHits.map((hit) => (
                    <li key={hit.sourceId}>
                      <a href={hit.pageUrl} target="_blank" rel="noreferrer" className="text-primary underline">
                        {hit.title}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {detail.fleetMatches.length > 0 && (
              <div>
                <h3 className="font-medium mb-2">{t('obdVehicles.fleetMatches')}</h3>
                <ul className="text-sm space-y-1">
                  {detail.fleetMatches.map((fleet) => (
                    <li key={`${fleet.registrationNumber}-${fleet.model}`}>
                      {fleet.registrationNumber} {fleet.brand} {fleet.model}
                      {fleet.forceNonObd ? ` — ${t('obdVehicles.forceNonObd')}` : ''}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ObdVehicleListPage;
